import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { Permission } from '@/types/auth.types';
import {
  MOBILE_QUEUE_IDS,
  MOBILE_TAB_IDS,
  MOBILE_TAB_LIMIT,
  type NavNode,
} from './nav-config';
import { useActiveNav, useVisibleNav } from './use-nav-state';
import type { ClinicRole } from './nav-config';
import { markSectionSwitch } from './nav-intent';

/**
 * Mobile tab bar.
 *
 * Kept custom deliberately — HeroUI v3 has no bottom-navigation
 * component, and this one is good: 44px touch targets, an active
 * pill, safe-area padding.
 *
 * What changed is where it gets its items. It now resolves
 * MOBILE_TAB_IDS against the same visible-nav tree the sidebar uses,
 * so a tab cannot point at a route the user's role can't open, and
 * cannot drift when a destination moves.
 */
export interface BottomNavProps {
  permissions: Permission[] | undefined;
  role?: ClinicRole;
  /** Slid away while the on-screen keyboard is up, as native tab bars are. */
  hidden?: boolean;
  onOpenDrawer: () => void;
}

export function BottomNav({
  permissions,
  role,
  hidden = false,
  onOpenDrawer,
}: BottomNavProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const sections = useVisibleNav(permissions, role);
  const { isLeafActive, isGroupActive } = useActiveNav();

  const tabs = useMemo(() => {
    const byId = new Map<string, NavNode>();
    sections.forEach((s) => s.items.forEach((i) => byId.set(i.id, i)));
    const queueIds =
      MOBILE_QUEUE_IDS[role ?? 'OTHER'] ?? MOBILE_QUEUE_IDS.OTHER;
    return MOBILE_TAB_IDS.map((id) =>
      id === 'queue'
        ? queueIds.map((q) => byId.get(q)).find(Boolean)
        : byId.get(id),
    )
      .filter((n): n is NavNode => n !== undefined)
      .slice(0, MOBILE_TAB_LIMIT);
  }, [sections, role]);

  return (
    <nav
      aria-label={t('nav.quickNav', 'Quick navigation')}
      inert={hidden}
      className={cn(
        'bottom-nav-blur fixed inset-x-0 bottom-0 z-(--z-bottom-nav) lg:hidden print:hidden',
        'border-t border-border-default',
        'transition-transform duration-(--shell-motion-slide) ease-(--ease-out-custom)',
        hidden && 'translate-y-full',
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex h-(--shell-bottom-nav-h) items-stretch justify-around px-1">
        {tabs.map((item) => {
          const active =
            item.kind === 'group' ? isGroupActive(item) : isLeafActive(item);
          const href =
            item.kind === 'group' ? item.activePrefixes[0] : item.href;
          const Icon = item.icon;
          const label =
            item.kind === 'link' && item.shortLabelKey
              ? t(item.shortLabelKey)
              : item.labelFallback
                ? t(item.labelKey, item.labelFallback)
                : t(item.labelKey);

          return (
            <NavLink
              key={item.id}
              to={href}
              aria-current={active ? 'page' : undefined}
              onClick={(e) => {
                // Tapping the tab you are already on goes back to its top,
                // as in every phone app, instead of doing nothing.
                if (pathname === href) {
                  e.preventDefault();
                  document
                    .getElementById('main-content')
                    ?.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                markSectionSwitch();
              }}
              className={cn(
                'no-select press-scale relative flex flex-1 flex-col items-center justify-center gap-0.5 px-1',
                'no-underline hover:no-underline',
                active ? 'text-brand-text' : 'text-secondary-400',
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-11 items-center justify-center rounded-lg',
                  'transition-colors duration-(--shell-motion-fast)',
                  active && 'bg-brand-soft',
                )}
              >
                <Icon aria-hidden className="size-icon-lg" />
              </span>
              <span className="text-micro font-semibold leading-none tracking-tight">
                {label}
              </span>
            </NavLink>
          );
        })}

        <button
          type="button"
          onClick={onOpenDrawer}
          aria-haspopup="dialog"
          className="no-select press-scale flex flex-1 flex-col items-center justify-center gap-0.5 px-2 text-secondary-400"
        >
          <span className="flex h-8 w-11 items-center justify-center">
            <Menu aria-hidden className="size-icon-lg" />
          </span>
          <span className="text-micro font-semibold leading-none">
            {t('nav.more', 'More')}
          </span>
        </button>
      </div>
    </nav>
  );
}
